/**
 * Stops on the live mission line.
 *
 * These are HTML markers, not a GL circle layer, for the same reason devices
 * are: a WebGL line that mounts after the dots will paint over them. Markers
 * sit in the overlay and stay readable. The name chip matches the device
 * pills and obeys the same "Icon names" switch.
 */
import React from 'react';
import { Box } from '@mui/material';
import { Marker } from 'react-map-gl';
import { useMapIconLabels, useMapLayerVisible } from '../../map/mapLayerVisibility';
import { pinLabelSide, pinLabelSx } from './pinLabels';
import { pinsFromWaypoints } from './missionWaypoints';

const WAYPOINT_BLUE = '#4c8bf5';

export { pinsFromWaypoints } from './missionWaypoints';
export type { MissionWaypointPin } from './missionWaypoints';

export interface MissionWaypointLayerProps {
  waypoints: Array<{
    id?: string | number;
    label?: string | null;
    latitude: number;
    longitude: number;
  }>;
  interactive?: boolean;
  onSelect?: (latitude: number, longitude: number) => void;
}

const MissionWaypointLayer: React.FC<MissionWaypointLayerProps> = ({
  waypoints,
  interactive = true,
  onSelect,
}) => {
  const visible = useMapLayerVisible('mission');
  const showIconLabels = useMapIconLabels();
  const pins = pinsFromWaypoints(waypoints);
  if (!visible || pins.length === 0) return null;

  const labelItems = pins.map((pin) => ({
    id: pin.id,
    latitude: pin.latitude,
    longitude: pin.longitude,
  }));

  return (
    <>
      {pins.map((pin) => {
        const labelSide = showIconLabels
          ? pinLabelSide(labelItems, pin.id, 'center')
          : 'hidden';
        const clickable = interactive && !!onSelect;
        return (
          <Marker
            key={pin.id}
            longitude={pin.longitude}
            latitude={pin.latitude}
            anchor="center"
            style={{ zIndex: 2, pointerEvents: interactive ? 'auto' : 'none' }}
          >
            <Box
              role={clickable ? 'button' : undefined}
              tabIndex={clickable ? 0 : -1}
              aria-label={pin.label}
              onClick={clickable ? () => onSelect?.(pin.latitude, pin.longitude) : undefined}
              onKeyDown={(e) => {
                if (!clickable) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSelect?.(pin.latitude, pin.longitude);
                }
              }}
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 18,
                height: 18,
                cursor: clickable ? 'pointer' : 'default',
              }}
            >
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  bgcolor: WAYPOINT_BLUE,
                  border: '2px solid #ffffff',
                  boxShadow: '0 1px 5px rgba(0,0,0,0.6)',
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  top: 18,
                  px: 0.6,
                  borderRadius: 0.75,
                  bgcolor: 'rgba(11,16,23,0.82)',
                  border: '1px solid rgba(255,255,255,0.14)',
                  whiteSpace: 'nowrap',
                  pointerEvents: 'none',
                  fontSize: '0.58rem',
                  fontWeight: 700,
                  lineHeight: 1.6,
                  color: '#e8eef7',
                  ...pinLabelSx(labelSide),
                }}
              >
                {pin.label}
              </Box>
            </Box>
          </Marker>
        );
      })}
    </>
  );
};

export default MissionWaypointLayer;
